import { useMutation } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceCacheUpdate from "common/datahooks/useWorkspaceCacheUpdate";
import { get } from "common/helpers/HTTP";
import type { WorkspaceAccount } from "common/types";

async function syncAccountRequest(
  workspaceId: string,
  accountId: string,
): Promise<Omit<WorkspaceAccount, "license" | "type">> {
  const { account } = await get(
    `workspaces/${workspaceId}/accounts/${accountId}/refresh`,
  );
  return account;
}

export default function useAccountSync() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { updateCachedWorkspaceAccounts } = useWorkspaceCacheUpdate();

  const { mutateAsync: syncAccount, isPending: isSyncingAccount } = useMutation(
    {
      mutationFn: ({ accountId }: { accountId: string }) =>
        syncAccountRequest(workspaceId, accountId),
      onSuccess: (updatedAccount) => {
        updateCachedWorkspaceAccounts(workspaceId, (draftAccounts) => {
          for (let i = 0; i < draftAccounts.length; i++) {
            if (draftAccounts[i].id === updatedAccount.id) {
              draftAccounts[i] = { ...draftAccounts[i], ...updatedAccount };
              return;
            }
          }
        });
      },
    },
  );

  return {
    syncAccount,
    isSyncingAccount,
  };
}
